/**
 * One issued certificate, read by its public identifier.
 *
 * Separate from `certificates.ts`, which is the signed-in learner's own list and the admin's
 * configuration of what a course awards. This is the SHARED artefact: fetched with no
 * credentials, by a visitor who is usually not the person who earned it.
 *
 *   GET /api/certificate/v1/certificates/<uuid>
 *
 * Ours rather than the platform's because the platform has no JSON API keyed by a uuid — its
 * `/api/certificates/v0/` routes are keyed by username, which the link does not carry. See the
 * backend module for the full reasoning and for what it refuses.
 */

import { openedx } from './openedx';

export interface CertificateSignatory {
  name: string;
  title: string;
  organization: string;
  /** A course asset path (`/asset-v1:…`), or '' when the signatory has no signature image. */
  image_url: string;
}

export interface IssuedCertificate {
  uuid: string;
  status: string;
  mode: string;
  /** A float as a string, e.g. "1.0". Empty when the course grades nothing. */
  grade: string;
  issued: string;
  recipient: { name: string };
  course: {
    id: string;
    title: string;
    number: string;
    org: string;
    org_name: string;
    end: string | null;
  };
  certificate: {
    name: string;
    description: string;
    /** Already resolved server-side: the certificate's own title, or the course's. */
    course_title: string;
    signatories: CertificateSignatory[];
  };
}

export const getIssuedCertificate = (uuid: string) =>
  openedx.get<IssuedCertificate>(
    `/api/certificate/v1/certificates/${encodeURIComponent(uuid)}`,
  );

/* ------------------------------------------------------- pathway certificates ---- */

/**
 * A PATHWAY certificate, from a different table, a different endpoint and a different
 * trigger — and rendered by the same document component.
 *
 *   GET /api/pathways/v1/certificates/<uuid>
 *
 * Public in exactly the same way and for exactly the same reason as the course one:
 * `authentication_classes = []` plus `AllowAny`, 404 for every refusal. The payload is
 * hand-built server-side, which is why it carries no course keys and no pathway key — see
 * `openedx_thinlms_pathways/certificates.py::public_payload`.
 */
export interface PathwayCertificateCourse {
  order: number;
  title: string;
  /** ISO date the learner finished that course, or null if it was never recorded. */
  completed_on: string | null;
}

export interface IssuedPathwayCertificate {
  uuid: string;
  kind: 'pathway';
  status: string;
  issued: string | null;
  recipient: { name: string };
  pathway: { title: string; academy_name: string; step_count: number };
  courses: PathwayCertificateCourse[];
}

export const getIssuedPathwayCertificate = (uuid: string) =>
  openedx.get<IssuedPathwayCertificate>(
    `/api/pathways/v1/certificates/${encodeURIComponent(uuid)}`,
  );

/* ------------------------------------------------------------- one document ---- */

/**
 * What the certificate PAGE renders, whichever kind it is.
 *
 * One shape, two sources. The alternative — a component that branches on kind all the way
 * down — would drift: the seal, the recipient line, the credential id and the print
 * stylesheet are the same document in both cases, and the only genuine difference is that
 * a pathway certificate lists the courses that earned it. So the difference is one
 * optional array, resolved HERE, and the page below stays a document rather than a switch.
 */
export interface CertificateDocument {
  kind: 'course' | 'pathway';
  uuid: string;
  issued: string | null;
  recipient: { name: string };
  /** The headline — the course's certificate title, or the pathway's name. */
  title: string;
  /** A second line under "for successfully completing", when there is one worth showing. */
  subject: string | null;
  /** The certificate configuration's own name, appended to the eyebrow. */
  label: string;
  description: string;
  signatories: CertificateSignatory[];
  /** The certifying organisation's readable name. Empty on a pathway certificate. */
  orgName: string;
  /** Only ever non-empty on a pathway certificate. */
  courses: PathwayCertificateCourse[];
  /**
   * The academy named on the document, as recorded AT ISSUE.
   *
   * Empty for a course certificate, where the live tenant name is used — that is what the
   * page has always done. A pathway certificate snapshots it, so a licensee that later
   * loses a licence, or an academy that renames itself, cannot change who a document says
   * issued it. The page prefers this when present and falls back to the live name.
   */
  issuerName: string;
}

export function courseDocument(certificate: IssuedCertificate): CertificateDocument {
  const title = certificate.certificate.course_title.trim();
  const courseTitle = certificate.course.title.trim();
  return {
    kind: 'course',
    uuid: certificate.uuid,
    issued: certificate.issued,
    recipient: certificate.recipient,
    title: title || courseTitle,
    // Printed under "for successfully completing" only when it says something the headline
    // does not. Usually the two hold the same string, and printing it twice reads as a
    // rendering fault rather than as a document.
    subject: courseTitle && title !== courseTitle ? courseTitle : null,
    label: certificate.certificate.name || '',
    description: certificate.certificate.description || '',
    signatories: certificate.certificate.signatories,
    orgName: certificate.course.org_name,
    courses: [],
    issuerName: '',
  };
}

export function pathwayDocument(certificate: IssuedPathwayCertificate): CertificateDocument {
  return {
    kind: 'pathway',
    uuid: certificate.uuid,
    issued: certificate.issued,
    recipient: certificate.recipient,
    title: certificate.pathway.title,
    subject: null,
    // Says what KIND of credential this is, in the eyebrow, beside the academy. Without it
    // a pathway certificate and a course certificate are indistinguishable at a glance, and
    // the whole point of this one is that it certifies more than a course.
    label: 'Learning path certificate',
    description: '',
    // A pathway has no signatory authoring anywhere in this product, so there is never a
    // list to render and the document signs with the academy — which is what a course
    // certificate already falls back to.
    signatories: [],
    orgName: '',
    courses: [...certificate.courses].sort((a, b) => a.order - b.order),
    issuerName: certificate.pathway.academy_name || '',
  };
}

/** "11 August 2026", or '' when the value will not parse. */
export function formatCertificateDate(value: string | null | undefined): string {
  if (!value) return '';
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return '';
  return new Date(parsed).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * The credential identifier as a person would read it out.
 *
 * The uuid is 32 undelimited hex characters, which is unreadable and un-dictatable. Grouping
 * it does not change the identity — the full value is still in the URL and still what
 * verifies — it just makes the number on the certificate quotable. Upper case for the same
 * reason: hex in lower case reads as machine output.
 */
export function credentialId(uuid: string): string {
  return (uuid.match(/.{1,4}/g) ?? [uuid]).join(' ').toUpperCase();
}

/**
 * Where to send someone who wants to share this certificate on LinkedIn.
 *
 * `share-offsite` deliberately, not LinkedIn's "add to profile" flow: that one needs an
 * organisation id issued to the certifying body, and inventing or omitting it produces a
 * form pre-filled with the wrong issuer. Sharing a link works for any deployment.
 */
export function linkedInShareUrl(pageUrl: string): string {
  return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
}
